<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="tw-price-details">
    <div
      v-if="options.description?.show && descriptionLabel.trim()"
      :style="descriptionStyle"
      class="tw-price-details__description"
    >
      {{ descriptionLabel }}
    </div>
    <table class="tw-price-details__table">
      <tbody>
        <tr v-if="packageTypeData.hasTrial">
          <th :style="trialTextStyle">
            {{ translate(`generate.trial.periods.${periodsData.trialPeriodType}`, locale, {
              count: periodsData.trialPeriod
            }) }}
          </th>
          <td :style="trialPriceStyle">
            {{ pricesData.trialPrice }}
          </td>
        </tr>
        <tr v-if="!isInlineRenewal">
          <th :style="periodTextStyle">
            <div>
              <span v-if="packageTypeData.hasTrial">{{
                translate(`generate.periods.thenEveryPeriods`, locale, {
                  period: translate(`generate.periods.withoutSingleCount.${periodsData.periodType}`, locale, { count: periodsData.period })
                })
              }}
              </span>
              <span v-else>{{ translate(`generate.subscription.periods.${periodsData.periodType}`, locale, { count: periodsData.period }) }}</span>
            </div>
            <div class="tw-price-details__table--secondary-text text-[10px] leading-4" :style="startingOnStyle">
              {{ translate(`generate.periods.startingOn`, locale, { date: startingOnDate }) }}
            </div>
          </th>
          <td :style="priceTextStyle">
            {{ pricesData.price }}
          </td>
        </tr>
      </tbody>
    </table>
    <div class="text-[8px] leading-none lg:text-[10px]">
      <span
        v-if="isInlineRenewal"
        v-html="purify(translate('generate.payment.priceDetails.inlineRenewalInfo', locale, {
          price: `<b>${pricesData.price}</b>`,
          period: `<b>${translate(`generate.periods.withoutSingleCount.${periodsData.periodType}`, locale, { count: periodsData.period })}</b>`,
          date: `<b>${startingOnDate}</b>`,
          cancelationLink: `<a href='//account.zotlo.com' target='_blank' class='!no-underline hover:!underline !text-[inherit]'>account.zotlo.com</a>`
        }))"
      />
      <span
        v-else
        v-html="purify(translate('generate.payment.priceDetails.renewalInfo', locale, {
          cancelationLink: `<a href='//account.zotlo.com' target='_blank' class='!no-underline hover:!underline !text-[inherit]'>account.zotlo.com</a>`
        }))"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { type PropType, computed } from 'vue';
import type { PageOptions } from '@zotlo-shared/types/model';
import type { ElementOptions } from '@zotlo-shared/types/options';
import { hexToRgba } from '@zotlo-shared/utils/helpers';
import { useTranslate } from '@zotlo-shared/composable/useTranslate';
import { getPackagePrices, getPackagePeriodsInfo, getPackageTypeConditions, addPeriod } from '@zotlo-shared/elements/common/payment/utils';
import { purify } from '@zotlo-shared/utils';

const props = defineProps({
  isEditorMode: { type: Boolean, default: true },
  config: { type: Object as PropType<ElementOptions['payment']>, default: () => ({}) },
  element: { type: Object, default: () => ({}) },
  pageData: { type: Object, default: () => ({}) },
  pageOptions: { type: Object as PropType<PageOptions>, default: () => ({}) },
  locale: { type: String, default: '' }
});

const { translate } = useTranslate();

// The heading text is stored in element.values so it goes through the localization
// pipeline; `undefined` means "never customized" -> localized default text, while
// an empty string means the user deliberately cleared it -> render nothing.
const customDescription = computed<string | undefined>(() => {
  try {
    return JSON.parse(props.element?.values || '{}')?.priceDetailsDescription;
  } catch {
    return undefined;
  }
});
const descriptionLabel = computed(() =>
  customDescription.value === undefined
    ? translate('generate.payment.priceDetails.description', props.locale)
    : customDescription.value
);

const options = computed(() => props.config?.priceDetails || {});
const isTrialUsed = computed(() => props.pageData?.isTrialUsed || false);
const pricesData = computed<Record<string, any>>(() => getPackagePrices(props.pageData?.selectedPackage, props.locale));
const periodsData = computed(() => getPackagePeriodsInfo(props.pageData?.selectedPackage, { ignoredDaysToWeek: [7] }));
const packageTypeData = computed(() => getPackageTypeConditions(props.pageData?.selectedPackage, { isTrialUsed: isTrialUsed.value }));

const paymentStartDate = computed(() => {
  const { trialPeriod, trialPeriodType } = periodsData.value;
  const today = new Date();

  return packageTypeData.value.hasTrial
    ? addPeriod(today, trialPeriod, trialPeriodType)
    : today;
});

const startingOnDate = computed(() =>
  paymentStartDate.value.toLocaleDateString(props.locale, { month: 'short', day: 'numeric' })
);

const priceDetailsStyle = computed(() => {
  const o = options.value;
  const backgroundColor = o?.background?.color || "#F6F7F9";
  const borderColor = o?.border?.color || "#E7EAEE";
  const textColor = o?.text?.color?.primary || "#0D0626";
  const secondaryTextColor = o?.text?.color?.secondary || "#818A9C";

  return {
    backgroundColor: hexToRgba(backgroundColor, o?.background?.opacity),
    borderColor,
    borderRadius: `${o?.border?.radius ?? 8}px`,
    borderWidth: `${o?.border?.width ?? 1}px`,
    textColor,
    secondaryTextColor,
    fontFamily: props.pageOptions?.fontFamily
  }
})

const primaryTextColor = computed(() => options.value?.text?.color?.primary || "#0D0626");

const descriptionStyle = computed(() => {
  const d = options.value?.description || {};
  const ts = d.textStyle || {};
  return {
    fontSize: `${d.fontSize ?? 16}px`,
    color: d.color || "#0D0626",
    fontWeight: ts.bold ? 600 : 400,
    fontStyle: ts.italic ? 'italic' : 'normal',
    textDecoration: ts.underline ? 'underline' : 'none'
  };
});

function buildCellStyle(style?: { fontSize?: number; color?: string }) {
  return {
    ...(style?.fontSize != null ? { fontSize: `${style.fontSize}px` } : {}),
    color: style?.color || primaryTextColor.value
  };
}

const trialTextStyle = computed(() => buildCellStyle(options.value?.trialText));
const trialPriceStyle = computed(() => buildCellStyle(options.value?.trialPrice));
const periodTextStyle = computed(() => buildCellStyle(options.value?.periodText));
const priceTextStyle = computed(() => buildCellStyle(options.value?.priceText));

// Inline renewal details is only shown when the package actually has a trial.
const isInlineRenewal = computed(() => !!options.value?.inlineRenewalDetails && packageTypeData.value.hasTrial);

// "starting on ..." uses the normal period text color at 50% opacity.
const startingOnStyle = computed(() => ({
  color: hexToRgba(options.value?.periodText?.color || primaryTextColor.value, 50)
}));
</script>

<style lang="postcss" scoped>
.tw-price-details {
  --borderColor: v-bind(priceDetailsStyle.borderColor);
  --secondaryTextColor: v-bind(priceDetailsStyle.secondaryTextColor);
  --textColor: v-bind(priceDetailsStyle.textColor);
  --fontFamily: v-bind(priceDetailsStyle.fontFamily);
  --bgColor: v-bind(priceDetailsStyle.backgroundColor);
  --borderWidth: v-bind(priceDetailsStyle.borderWidth);
  --borderRadius: v-bind(priceDetailsStyle.borderRadius);

  background-color: var(--bgColor);
  border-width: var(--borderWidth);
  border-style: solid;
  border-color: var(--borderColor);
  border-radius: var(--borderRadius);
  font-family: var(--fontFamily);
  color: var(--textColor);

  @apply px-[12px] py-[8px] flex flex-col gap-[6px];

  &__description {
    @apply block mb-[4px] text-center;
    /* Long text (even a single unbroken word) must wrap instead of widening the card;
       overflow-wrap: anywhere also shrinks the min-content width for the flex parent. */
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  &__table {
    @apply w-full ;

    td, th {
      @apply py-[6px] border-b;
      border-color: var(--borderColor);
    }

    th {
      @apply text-left text-[12px] leading-none font-medium;
    }

    td {
      @apply text-right text-[14px] leading-none font-semibold;
    }

    &--secondary-text {
      color: var(--secondaryTextColor);
    }
  }
}

[dir="rtl"] .tw-price-details {
  &__table {
    th { @apply text-right; }
    td { @apply text-left; }
  }
}
</style>
